import React, { useState, useEffect } from 'react';
import { useParams, useNavigate } from 'react-router-dom';
import apiService from '../services/api';
import { setupWeChatShare, isWeChat } from '../utils/wechat';

// 本地时间格式化，不使用 toISOString 避免 UTC 偏移
const formatDateTimeLocal = (isoStr) => {
  const d = new Date(isoStr);
  const pad = (n) => n.toString().padStart(2, '0');
  return `${d.getFullYear()}-${pad(d.getMonth()+1)}-${pad(d.getDate())} ${pad(d.getHours())}:${pad(d.getMinutes())}`;
};

const ActivityDetail = () => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [activity, setActivity] = useState(null);
  const [isRegistered, setIsRegistered] = useState(false);
const [userActivityStatus, setUserActivityStatus] = useState(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');
  const [registerLoading, setRegisterLoading] = useState(false);
  const [registrations, setRegistrations] = useState([]);
  const [registrationsLoading, setRegistrationsLoading] = useState(false);

  useEffect(() => {
    // 只负责拉取数据
    const fetchActivity = async () => {
      try {
        setLoading(true);
        // 获取活动详情
        const activityResponse = await apiService.getActivity(id);
        // 判断当前用户是否管理员或工会委员（通过本地存储user.role）
        const user = JSON.parse(localStorage.getItem('user') || '{}');
        const is_admin = user.role === 'admin';
        const is_union = user.role === 'union';
        setActivity({ ...activityResponse.data, is_admin, is_union });
        // 检查用户是否已报名，并记录报名状态
        const userActivitiesResponse = await apiService.getUserActivities();
        const userActivity = userActivitiesResponse.data.find(ua => ua.activity_id === parseInt(id));
        setIsRegistered(!!userActivity && userActivity.status === 'registered');
        setUserActivityStatus(userActivity ? userActivity.status : null);
        // 获取活动报名人员列表
        fetchRegistrations();
      } catch (err) {
        setError('获取活动详情失败');
        console.error(err);
      } finally {
        setLoading(false);
      }
    };
    fetchActivity();
  }, [id]);

  useEffect(() => {
    // 只在 activity 加载后调用分享
    if (activity) {
      setupWeChatShare({
        title: activity.name,
        desc: activity.description || '活动详情',
        link: window.location.href,
        imgUrl: activity.thumbnail,
        type: 'activity',
      });
    }
  }, [activity]);

  const fetchRegistrations = async () => {
    try {
      setRegistrationsLoading(true);
      const response = await apiService.getActivityRegistrations(id);
      setRegistrations(response.data);
    } catch (err) {
      console.error('获取报名人员列表失败', err);
    } finally {
      setRegistrationsLoading(false);
    }
  };

  const isActiveActivity = (activity) => {
    const now = new Date();
    const startTime = new Date(activity.start_time);
    const endTime = new Date(activity.end_time);
    return startTime <= now && now <= endTime;
  };

  const handleRegister = async () => {
    try {
      setRegisterLoading(true);
      await apiService.registerActivity(id);
      setIsRegistered(true);
      setUserActivityStatus('registered');
      // 报名成功后刷新报名人员列表
      fetchRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || '报名失败');
      console.error(err);
    } finally {
      setRegisterLoading(false);
    }
  };

  const handleCancelRegistration = async () => {
    try {
      setRegisterLoading(true);
      await apiService.cancelRegistration(id);
      setIsRegistered(false);
      setUserActivityStatus(null); // 用户主动取消后，报名记录被删除
      // 取消报名后刷新报名人员列表
      fetchRegistrations();
    } catch (err) {
      setError(err.response?.data?.message || '取消报名失败');
      console.error(err);
    } finally {
      setRegisterLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="text-center my-5">
        <div className="spinner-border text-primary" role="status">
          <span className="visually-hidden">Loading...</span>
        </div>
        <p className="mt-2">加载中...</p>
      </div>
    );
  }

  if (!activity) {
    return <div className="alert alert-danger">活动不存在</div>;
  }

  return (
    <div>
      <div className="card">
        <div className="card-header position-relative" style={{minHeight: 56}}>
          <h2 className="mb-0 text-center w-100" style={{fontWeight: 600}}>
            {activity.name}
            {isActiveActivity(activity) ? (
              <span className="badge bg-success ms-2">进行中</span>
            ) : new Date(activity.start_time) > new Date() ? (
              <span className="badge bg-warning ms-2">未开始</span>
            ) : (
              <span className="badge bg-secondary ms-2">已结束</span>
            )}
          </h2>
          {isWeChat() && (
            <div
              className="alert alert-info mt-2 py-1 px-2"
              style={{
                fontSize: '1em',
                border: '1px solid #bee5eb',
                background: '#e8f7fa',
                borderRadius: 6,
                maxWidth: 320,
                margin: '0 auto',
                textAlign: 'center',
                position: 'static'
              }}
            >
              点击右上角“...”，即可分享本活动
            </div>
          )}
        </div>
        <div className="card-body">
          {error && <div className="alert alert-danger">{error}</div>}
          
          <div className="row mb-4">
            {/* 左侧缩略图 */}
            <div className="col-md-4 mb-3 mb-md-0 d-flex align-items-stretch">
              <div className="w-100 d-flex align-items-center justify-content-center" style={{minHeight: 260}}>
                {activity.thumbnail ? (
                  <img src={activity.thumbnail} alt="活动缩略图" className="img-fluid rounded shadow-sm w-100 h-100" style={{maxHeight: 260, maxWidth: '100%', objectFit: 'cover', borderRadius: '0.5rem'}} />
                ) : (
                  <div className="bg-light border rounded d-flex align-items-center justify-content-center w-100 h-100" style={{height: 260}}>
                    <span className="text-muted">暂无缩略图</span>
                  </div>
                )}
              </div>
            </div>
            {/* 中间活动详情 */}
            <div className="col-md-4 mb-3 mb-md-0">
              <h4 className="mb-3">{activity.name}</h4>
              <div className="mb-2">
                {isActiveActivity(activity) ? (
                  <span className="badge bg-success me-2">进行中</span>
                ) : new Date(activity.start_time) > new Date() ? (
                  <span className="badge bg-warning me-2">未开始</span>
                ) : (
                  <span className="badge bg-secondary me-2">已结束</span>
                )}
              </div>
              <p className="mb-3">{activity.description}</p>
              <h5>活动时间</h5>
              <p>
                <strong>开始时间：</strong> {new Date(activity.start_time).toLocaleString()}<br />
                <strong>结束时间：</strong> {new Date(activity.end_time).toLocaleString()}
              </p>
            </div>
            {/* 右侧积分卡片 */}
            <div className="col-md-4 d-flex align-items-stretch">
              <div className="card w-100 h-100" style={{minHeight: 260}}>
                <div className="card-header bg-primary text-white">
                  <h5 className="mb-0">活动积分</h5>
                </div>
                <div className="card-body text-center d-flex flex-column justify-content-between h-100">
                  <div>
                    <h2 className="display-4">{activity.points}</h2>
                    <p className="text-muted">完成活动后可获得的积分</p>
                  </div>
                  <div>
                    {userActivityStatus === 'completed' ? (
                      <div className="alert alert-secondary mb-2">活动已结束，您已报名</div>
                    ) : userActivityStatus === 'deleted' ? (
                      <div className="alert alert-danger mb-2">您的报名已被取消</div>
                    ) : userActivityStatus === 'registered' ? (
                      <>
                        <div className="alert alert-success mb-2">
                          您已成功报名此活动
                        </div>
                        <button 
                          className="btn btn-danger btn-lg w-100" 
                          onClick={handleCancelRegistration}
                          disabled={registerLoading}
                        >
                          {registerLoading ? '取消中...' : '取消报名'}
                        </button>
                      </>
                    ) : !activity.enroll_open ? (
                      <div className="alert alert-warning">
                        当前活动已关闭报名，无法报名
                      </div>
                    ) : (
                      <button 
                        className="btn btn-primary btn-lg w-100" 
                        onClick={handleRegister}
                        disabled={registerLoading}
                      >
                        {registerLoading ? '报名中...' : '立即报名'}
                      </button>
                    ) }
                  </div>
                </div>
              </div>
            </div>
          </div>
          
          {/* 报名人员列表 */}
          <div className="mt-4">
            <div className="d-flex justify-content-between align-items-center mb-2">
              <h4>当前报名人员列表</h4>
              {activity && (activity.is_admin || activity.is_union) && (
                <button className="btn btn-outline-primary btn-sm" onClick={() => navigate(`/activities/${activity.id}/import-solitaire`)}>
                  导入接龙名单
                </button>
              )}
            </div>
            {registrationsLoading ? (
              <div className="text-center my-3">
                <div className="spinner-border spinner-border-sm text-primary" role="status">
                  <span className="visually-hidden">加载中...</span>
                </div>
                <span className="ms-2">加载人员列表...</span>
              </div>
            ) : registrations.length === 0 ? (
              <div className="alert alert-info">暂无人员报名此活动</div>
            ) : (
              <div className="table-responsive">
                <table className="table table-striped table-hover">
                  <thead>
                    <tr>
                      <th>序号</th>
                      <th>用户名</th>
                      <th>状态</th>
                      <th>报名时间</th>
                    </tr>
                  </thead>
                  <tbody>
                    {registrations.map((reg, index) => (
                      <tr key={reg.id}>
                        <td>{index + 1}</td>
                        <td>{reg.nickname || reg.username || '未知用户'}</td>
                        <td>
                          {reg.status === 'registered' && <span className="badge bg-warning">已报名</span>}
                          {reg.status === 'attended' && <span className="badge bg-info">已参加</span>}
                          {reg.status === 'completed' && <span className="badge bg-success">已完成</span>}
                          {reg.status === 'deleted' && <span className="badge bg-danger">已取消</span>}
                        </td>
                        <td>{formatDateTimeLocal(reg.registered_at)}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
            )}
          </div>
          
          <button className="btn btn-secondary mt-3" onClick={() => navigate(-1)}>
            返回
          </button>
        </div>
      </div>
    </div>
  );
};

export default ActivityDetail;
